/**
 * InviteAdminDialog — email + role + language → POST /admin/invitations
 * (P1 Tier-A, users.invite_admin). BottomSheet container. Role options are the
 * 5 system roles (localized via roles.<slug>); language is RU/KY only.
 */
import { useCallback, type ReactElement } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { BottomSheet, Button, Field } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { useCreateInvitation } from "@/features/admin/invitations/hooks";
import {
  SYSTEM_ROLE_SLUGS,
  type InvitationResponse,
  type SystemRoleSlug,
} from "@/features/admin/invitations/api";

const schema = z.object({
  email: z.string().email("formError.email"),
  role_slug: z.enum(SYSTEM_ROLE_SLUGS),
  language: z.enum(["ru", "ky"]),
});
type FormValues = z.infer<typeof schema>;

const SELECT_CLASS =
  "min-h-[44px] w-full rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]";

export interface InviteAdminDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (res: InvitationResponse) => void;
}

export function InviteAdminDialog({ open, onClose, onCreated }: InviteAdminDialogProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const create = useCreateInvitation();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", role_slug: "support_agent" as SystemRoleSlug, language: "ru" },
  });

  const errText = (key?: string): string | undefined => (key ? t(key) : undefined);

  const submit = useCallback(
    (values: FormValues) => {
      create.mutate(values, {
        onSuccess: (res) => { reset(); onCreated(res); },
      });
    },
    [create, reset, onCreated],
  );

  return (
    <BottomSheet open={open} onClose={onClose} title={t("invitations.form.title")}>
      <form data-testid="invite-form" onSubmit={handleSubmit(submit)} className="flex flex-col gap-4">
        <Field label={t("invitations.form.email")} type="email" data-testid="invite-email" error={errText(errors.email?.message)} {...register("email")} />
        <div className="flex flex-col gap-1.5">
          <label htmlFor="invite-role" className="text-[13px] font-semibold text-foreground">{t("invitations.form.role")}</label>
          <select id="invite-role" data-testid="invite-role" className={SELECT_CLASS} {...register("role_slug")}>
            {SYSTEM_ROLE_SLUGS.map((slug) => (
              <option key={slug} value={slug}>{t(`roles.${slug}`)}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="invite-language" className="text-[13px] font-semibold text-foreground">{t("invitations.form.language")}</label>
          <select id="invite-language" data-testid="invite-language" className={SELECT_CLASS} {...register("language")}>
            <option value="ru">{t("invitations.form.langRu")}</option>
            <option value="ky">{t("invitations.form.langKy")}</option>
          </select>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={create.isPending} className="min-h-[44px]">{t("common.cancel")}</Button>
          <Button type="submit" variant="primary" size="sm" data-testid="invite-submit" loading={create.isPending} className="min-h-[44px]">{t("invitations.form.submit")}</Button>
        </div>
      </form>
    </BottomSheet>
  );
}

export default InviteAdminDialog;
